import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeClass, DiffResult } from '@drawing2d/core';
import type { ComparisonSheet } from '../compare.js';
import { t, type Lang } from '../i18n.js';

/**
 * The stage: one sheet, shown three ways.
 *
 * The whole point of this component is that **before, after and diff are the
 * same picture**. They are drawn into one coordinate system — the union of both
 * versions' page boxes, computed once in `compare.ts` — and zoom and pan are a
 * single CSS transform on a single container holding the backdrop and the
 * overlay together. Neither is applied to the marks and the image separately,
 * because two transforms are two rounding paths, and a half-pixel of drift
 * between the image and the marks reads as the drawing having moved. With one
 * transform it cannot happen: the container moves or nothing does.
 *
 * Filtering hides marks by `data-change-class`, never by hiding a layer. A
 * class's ghost, its leader and its badge each carry the attribute, so they
 * leave and return with the geometry they explain. Hiding the `changed` layer
 * instead would leave that class's ghosts on the page annotating nothing.
 *
 * The stage carries its frame, zoom and tolerance as data attributes. That is
 * not decoration: it is how a browser test converts the matching tolerance into
 * pixels and asserts registration in the units the requirement is stated in.
 */

export type CompareView = 'before' | 'after' | 'diff';

/**
 * How far in to go when a change is selected from the fitted view.
 *
 * Enough that a single entity is legible on a laptop, not so far that its
 * surroundings leave the frame — a change you cannot see in context is a change
 * you cannot judge.
 */
const SELECTION_ZOOM = 3;

export function CompareStage({
  sheet,
  diff,
  view,
  lang,
  hidden,
  monochrome,
  baseImage,
  compareImage,
  entryOf,
  onSelect,
  selected,
}: {
  sheet: ComparisonSheet;
  diff: DiffResult;
  view: CompareView;
  lang: Lang;
  hidden: ReadonlySet<ChangeClass>;
  monochrome: boolean;
  baseImage: string | null;
  compareImage: string | null;
  /** The (version, page, identifier) lookup. An identifier alone locates nothing. */
  entryOf: (side: 'base' | 'compare', pageIndex: number, entityId: string) => number | null;
  /** Called with the index into `diff.entries`, never with an entity identifier. */
  onSelect: (entryIndex: number) => void;
  /** The selected index into `diff.entries`, or null. Drawn, not only reported. */
  selected: number | null;
}): JSX.Element {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const stageRef = useRef<HTMLDivElement>(null);
  const marksRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const dragged = useRef(false);

  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);

  // Zoom about the centre of the stage, so the thing being looked at stays put.
  const zoomBy = useCallback(
    (factor: number) => {
      const box = stageRef.current?.getBoundingClientRect();
      const cx = (box?.width ?? 0) / 2;
      const cy = (box?.height ?? 0) / 2;
      const next = Math.min(8, Math.max(1, zoom * factor));
      const k = next / zoom;
      setPan({ x: cx - (cx - pan.x) * k, y: cy - (cy - pan.y) * k });
      setZoom(next);
    },
    [pan, zoom],
  );

  const fit = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  /**
   * The join from a clicked mark back to the comparison.
   *
   * On the diff view a mark carries `data-entry`, an index into `diff.entries`,
   * and that is used directly. On the before and after views a mark carries only
   * `data-entity-id`, which names nothing on its own — both versions contain
   * "p0-1" and they are different entities. So the lookup there is by the triple
   * (which version, which page, which identifier), and never by the identifier.
   */
  const select = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (dragged.current) {
        dragged.current = false;
        return;
      }
      const target = event.target as Element | null;
      const mark = target?.closest('[data-entry], [data-entity-id]');
      if (!mark) return;

      const entry = mark.getAttribute('data-entry');
      if (entry !== null) {
        const index = Number(entry);
        if (Number.isFinite(index)) onSelect(index);
        return;
      }

      const entityId = mark.getAttribute('data-entity-id');
      if (entityId === null) return;
      const side = view === 'before' ? 'base' : 'compare';
      const pageIndex = view === 'before' ? sheet.basePageIndex : sheet.comparePageIndex;
      if (pageIndex === null) return;
      const index = entryOf(side, pageIndex, entityId);
      if (index !== null) onSelect(index);
    },
    [entryOf, onSelect, sheet.basePageIndex, sheet.comparePageIndex, view],
  );

  const markup = view === 'before' ? sheet.before : view === 'after' ? sheet.after : sheet.diff;

  /**
   * The selected change, pointed at on the drawing.
   *
   * Choosing a change and being shown its properties, with no indication of
   * *where* on the sheet it is, leaves the reviewer to find a mark among sixty by
   * matching a dash pattern — which is most of what made this surface hard to
   * read. Every mark the overlay emits carries `data-entry`, the index into
   * `diff.entries`, and that is the only reliable key: an entity identifier names
   * a different entity in each version.
   *
   * Done here rather than in the overlay because the overlay is an artefact. It is
   * hashed, compared between runs and downloaded, so a transient interface state
   * must not change its bytes.
   */
  const key = selected === null ? null : String(selected).padStart(4, '0');
  useEffect(() => {
    const root = marksRef.current;
    if (!root) return;

    let picked: SVGGraphicsElement | null = null;
    for (const node of root.querySelectorAll('[data-entry]')) {
      const mine = node.getAttribute('data-entry') === key;
      node.classList.toggle('picked', key !== null && mine);
      node.classList.toggle('unpicked', key !== null && !mine);
      if (mine && node.getAttribute('data-role') === 'geometry' && !picked) {
        picked = node as SVGGraphicsElement;
      }
    }
    if (!picked) return;

    /*
      Bring it into view.

      Dimming the other marks without moving to the selected one is worse than
      doing nothing: the sheet goes quiet and the reviewer still has to find the
      change. Centred in *canvas* fractions rather than by nudging the current pan,
      so repeated selections cannot accumulate rounding, and zoomed in only from
      the fitted view — once the reviewer has chosen a zoom, that is their choice.
    */
    let box: { x: number; y: number; width: number; height: number };
    try {
      box = picked.getBBox();
    } catch {
      // getBBox throws on an element that is not rendered. Nothing to centre on.
      return;
    }
    const fx = (box.x + box.width / 2) / sheet.frame.widthPt;
    const fy = (box.y + box.height / 2) / sheet.frame.heightPt;
    if (!Number.isFinite(fx) || !Number.isFinite(fy)) return;

    const stage = stageRef.current;
    if (!stage) return;
    const { width, height } = stage.getBoundingClientRect();
    const z = zoom === 1 ? SELECTION_ZOOM : zoom;

    // The canvas transform-origin is its top-left corner, so a canvas point p
    // lands at `z·p + pan`. Centring it means pan = centre − z·p. Assuming a
    // centred origin instead put the mark off the sheet and left the stage blank.
    setZoom(z);
    setPan({ x: width / 2 - z * fx * width, y: height / 2 - z * fy * height });

    // `markup` is a dependency because replacing the HTML replaces these nodes, so
    // switching view or sheet would otherwise drop the emphasis silently. `zoom`
    // is deliberately not one: reading it to decide whether to zoom, then setting
    // it, would re-centre the view on every zoom the reviewer performs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, markup, sheet.frame.widthPt, sheet.frame.heightPt]);
  // The diff view is drawn over the after page where there is one, because the
  // after page is what the reviewer is being asked to accept. A removed sheet
  // has no after page, so it falls back to the before page and says so.
  const backdropPage = view === 'before' ? sheet.basePage : (sheet.comparePage ?? sheet.basePage);
  const backdrop =
    view === 'before'
      ? baseImage
      : view === 'after'
        ? compareImage
        : (compareImage ?? baseImage);
  const backdropIsAfter = view !== 'before' && sheet.comparePage !== null;

  const classes = ['compare-stage'];
  if (monochrome) classes.push('monochrome');
  for (const c of hidden) classes.push(`hide-${c}`);

  if (markup === null) {
    return (
      <div className="compare-stage-wrap">
        <StageBar
          lang={lang}
          zoom={zoom}
          onZoomIn={() => zoomBy(1.5)}
          onZoomOut={() => zoomBy(1 / 1.5)}
          onFit={fit}
        />
        <p className="stage-absent" data-absent={view}>
          {view === 'before' ? s('sheetAbsentBefore') : s('sheetAbsentAfter')}
        </p>
      </div>
    );
  }

  return (
    <div className="compare-stage-wrap">
      <StageBar
        lang={lang}
        zoom={zoom}
        onZoomIn={() => zoomBy(1.5)}
        onZoomOut={() => zoomBy(1 / 1.5)}
        onFit={fit}
      />
      <div
        ref={stageRef}
        className={classes.join(' ')}
        data-view={view}
        data-sheet={sheet.index}
        data-zoom={zoom}
        data-frame-width={sheet.frame.widthPt}
        data-frame-height={sheet.frame.heightPt}
        data-tolerance={diff.tolerance}
        data-displacement-unit={diff.displacementUnit}
        // Capped by width from the frame's ratio, for the same reason as the
        // single-drawing stage: a capped height squashes the box and the backdrop
        // and the marks then letterbox independently.
        style={{
          aspectRatio: `${sheet.frame.widthPt} / ${sheet.frame.heightPt}`,
          maxWidth: `calc(var(--stage-cap) * ${sheet.frame.widthPt} / ${sheet.frame.heightPt})`,
        }}
        onPointerDown={(e) => {
          drag.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
          dragged.current = false;
        }}
        onPointerMove={(e) => {
          const from = drag.current;
          if (!from) return;
          const dx = e.clientX - from.x;
          const dy = e.clientY - from.y;
          if (!dragged.current && Math.hypot(dx, dy) <= 4) return;
          dragged.current = true;
          setPan({ x: from.panX + dx, y: from.panY + dy });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerLeave={() => {
          drag.current = null;
        }}
        onClick={select}
      >
        {/* One transform, one container. The backdrop and the marks cannot drift. */}
        <div
          className="compare-canvas"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        >
          {backdrop && backdropPage && (
            <img
              className="compare-backdrop"
              src={backdrop}
              alt=""
              draggable={false}
              // Placed at its own size inside the shared frame, never stretched
              // to fill it: a smaller sheet is a smaller sheet.
              style={{
                width: `${(backdropPage.widthPt / sheet.frame.widthPt) * 100}%`,
                height: `${(backdropPage.heightPt / sheet.frame.heightPt) * 100}%`,
              }}
            />
          )}
          <div ref={marksRef} className="compare-marks" dangerouslySetInnerHTML={{ __html: markup }} />
        </div>
      </div>
      <p className="note stage-note">
        {backdropIsAfter ? s('backdropAfter') : s('backdropBefore')} {s('panHint')}
      </p>
    </div>
  );
}

function StageBar({
  lang,
  zoom,
  onZoomIn,
  onZoomOut,
  onFit,
}: {
  lang: Lang;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);
  return (
    <div className="stage-bar">
      <button className="zoom" data-zoom-action="out" onClick={onZoomOut}>
        {s('zoomOut')}
      </button>
      <button className="zoom" data-zoom-action="in" onClick={onZoomIn}>
        {s('zoomIn')}
      </button>
      <button className="zoom" data-zoom-action="fit" onClick={onFit}>
        {s('resetView')}
      </button>
      <span className="zoom-readout" data-zoom-readout={zoom}>
        {zoom.toFixed(2)}×
      </span>
    </div>
  );
}
